import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Alert,
    IconButton,
    Button,
    Grid,
    Paper,
    Tooltip,
    LinearProgress,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    Security as SecurityIcon,
    Warning as WarningIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Shield as ShieldIcon,
    BugReport as BugIcon,
} from '@mui/icons-material';
import AttackDetectionService, { DetectedAttack } from '../services/attackDetectionService';
import RateLimitingService from '../services/rateLimitingService';

const AttackMonitor: React.FC = () => {
    const [attacks, setAttacks] = useState<DetectedAttack[]>([]);
    const [stats, setStats] = useState<any>(null);
    const [rateLimitStats, setRateLimitStats] = useState<any>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedAttack, setSelectedAttack] = useState<DetectedAttack | null>(null);

    const loadData = useCallback(() => {
        const detectedAttacks = AttackDetectionService.getDetectedAttacks();
        const attackStats = AttackDetectionService.getAttackStatistics();
        const rateLimits = RateLimitingService.getStatistics();

        setAttacks(detectedAttacks);
        setStats(attackStats);
        setRateLimitStats(rateLimits);
    }, []);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 5000); // Refresh every 5 seconds
        return () => clearInterval(interval);
    }, [loadData]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        loadData();
        setTimeout(() => setIsRefreshing(false), 500);
    }, [loadData]);

    const handleBlockIP = useCallback((ip: string) => {
        AttackDetectionService.blockIP(ip);
        RateLimitingService.blockIP(ip);
        loadData();
    }, [loadData]);

    const handleUnblockIP = useCallback((ip: string) => {
        AttackDetectionService.unblockIP(ip);
        RateLimitingService.unblockIP(ip);
        loadData();
    }, [loadData]);

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return 'error';
            case 'high':
                return 'warning';
            case 'medium':
                return 'info';
            case 'low':
                return 'success';
            default:
                return 'default';
        }
    };

    const getAttackIcon = (type: string) => {
        switch (type) {
            case 'brute_force':
                return <WarningIcon />;
            case 'ddos':
                return <ErrorIcon />;
            case 'sql_injection':
                return <BugIcon />;
            case 'xss':
                return <BugIcon />;
            case 'path_traversal':
                return <BugIcon />;
            default:
                return <SecurityIcon />;
        }
    };

    const exportAttacks = useCallback(() => {
        const data = JSON.stringify(attacks, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attacks-${new Date().toISOString()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [attacks]);

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ShieldIcon /> Attack Monitor
                </Typography>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportAttacks}
                        disabled={attacks.length === 0}
                    >
                        Export
                    </Button>
                </Box>
            </Box>

            {/* Statistics Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Total Attacks
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                                {stats?.total || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                All time
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Recent Attacks
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                {stats?.recent || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Last hour
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Auto-Blocked
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                                {stats?.autoBlocked || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Automatically blocked
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                Rate Limited IPs
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                                {rateLimitStats?.blockedIPs || 0}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Currently blocked
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Attack Types Distribution */}
            {stats && stats.byType && Object.keys(stats.byType).length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" mb={2}>
                            Attack Types Distribution
                        </Typography>
                        <Grid container spacing={2}>
                            {Object.entries(stats.byType).map(([type, count]: [string, any]) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={type}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center" gap={1}>
                                            {getAttackIcon(type)}
                                            <Typography variant="body2">
                                                {type.replace(/_/g, ' ').toUpperCase()}
                                            </Typography>
                                        </Box>
                                        <Chip label={count} size="small" color="error" />
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min((count / stats.total) * 100, 100)}
                                        sx={{ mt: 1 }}
                                        color="error"
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Recent Attacks List */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" mb={2}>
                        Recent Attacks ({attacks.length})
                    </Typography>

                    {attacks.length === 0 ? (
                        <Alert severity="success" icon={<CheckCircleIcon />}>
                            No attacks detected. Your system is secure!
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Time</TableCell>
                                        <TableCell>IP Address</TableCell>
                                        <TableCell>Attack Type</TableCell>
                                        <TableCell>Severity</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {attacks.slice(0, 20).map((attack) => (
                                        <TableRow
                                            key={attack.id}
                                            hover
                                            onClick={() => setSelectedAttack(attack)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell>
                                                {new Date(attack.timestamp).toLocaleTimeString()}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {attack.ip}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {getAttackIcon(attack.type)}
                                                    <Typography variant="body2">
                                                        {attack.type.replace(/_/g, ' ')}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={attack.severity}
                                                    size="small"
                                                    color={getSeverityColor(attack.severity) as any}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {attack.blocked ? (
                                                    <Chip
                                                        label={attack.autoBlocked ? 'Auto-blocked' : 'Blocked'}
                                                        size="small"
                                                        color="error"
                                                        icon={<BlockIcon />}
                                                    />
                                                ) : (
                                                    <Chip label="Active" size="small" color="warning" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {attack.blocked ? (
                                                    <Tooltip title="Unblock IP">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleUnblockIP(attack.ip);
                                                            }}
                                                        >
                                                            <CheckCircleIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Block IP">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleBlockIP(attack.ip);
                                                            }}
                                                        >
                                                            <BlockIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Attack Details Dialog */}
            {selectedAttack && (
                <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" mb={2}>
                            Attack Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    IP Address:
                                </Typography>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                    {selectedAttack.ip}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Attack Type:
                                </Typography>
                                <Typography variant="body1">
                                    {selectedAttack.type.replace(/_/g, ' ').toUpperCase()}
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Severity:
                                </Typography>
                                <Chip
                                    label={selectedAttack.severity}
                                    size="small"
                                    color={getSeverityColor(selectedAttack.severity) as any}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Timestamp:
                                </Typography>
                                <Typography variant="body1">
                                    {new Date(selectedAttack.timestamp).toLocaleString()}
                                </Typography>
                            </Grid>
                            {selectedAttack.details && (
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Details:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                                        <pre style={{ margin: 0, fontSize: '0.875rem', overflow: 'auto' }}>
                                            {JSON.stringify(selectedAttack.details, null, 2)}
                                        </pre>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                        <Box mt={2} display="flex" gap={1}>
                            <Button
                                variant="outlined"
                                onClick={() => setSelectedAttack(null)}
                            >
                                Close
                            </Button>
                            {!selectedAttack.blocked && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<BlockIcon />}
                                    onClick={() => {
                                        handleBlockIP(selectedAttack.ip);
                                        setSelectedAttack(null);
                                    }}
                                >
                                    Block IP
                                </Button>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default AttackMonitor;
